import { c as _c } from "react/compiler-runtime";
import type { KeyWith } from '@/lib/piano/keyboard';
import { feltBalanceWeight } from '@/lib/piano/touch-design/felt-balance-weight';
import { isOctaveStart } from '@/lib/piano/keyboard/keyboard-geometry';
import { cn } from '@/lib/utils';
import type { TouchWeightKeyAnalysis } from '@/lib/piano/touch-design/touch-weight-key-analysis';
import { AnalyzeDataSheetTableCell } from './AnalyzeDataSheetCell';
export interface AnalyzeDataSheetTableRowProps {
  isBlackKey: boolean;
  item: KeyWith<TouchWeightKeyAnalysis>;
  locale: string;
  shouldUseManualSWRMeasurements: boolean;
  shouldUseSupportSpringMeasurements: boolean;
}
export const AnalyzeDataSheetTableRow: React.FC<AnalyzeDataSheetTableRowProps> = t0 => {
  const $ = _c(62);
  const {
    isBlackKey,
    item,
    locale,
    shouldUseManualSWRMeasurements,
    shouldUseSupportSpringMeasurements
  } = t0;
  let t1;
  if ($[0] !== item) {
    t1 = cn("border-b border-border last:border-b-0 even:bg-secondary/50 hover:bg-accent", isOctaveStart(item) && "[&>td]:border-t [&>td]:border-t-input");
    $[0] = item;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== item.number) {
    t2 = <td className="px-2 py-[7px] font-mono text-[12px] text-ptl-ink-3">{item.number}</td>;
    $[2] = item.number;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== isBlackKey || $[5] !== item.name) {
    t3 = isBlackKey ? <span className="inline-flex rounded-sm bg-ptl-key-black px-[5px] py-px text-ptl-key-white">{item.name}</span> : item.name;
    $[4] = isBlackKey;
    $[5] = item.name;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <td className="px-2 py-[7px] font-mono text-[13px]">{t3}</td>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== item.payload.downWeightWithSpringSupport || $[10] !== item.payload.supportSpringBalanceWeight || $[11] !== locale || $[12] !== shouldUseSupportSpringMeasurements) {
    t5 = shouldUseSupportSpringMeasurements && <AnalyzeDataSheetTableCell locale={locale} value={item.payload.supportSpringBalanceWeight === null ? null : item.payload.downWeightWithSpringSupport} />;
    $[9] = item.payload.downWeightWithSpringSupport;
    $[10] = item.payload.supportSpringBalanceWeight;
    $[11] = locale;
    $[12] = shouldUseSupportSpringMeasurements;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== item.payload.downWeightWithoutSpringSupport || $[15] !== locale) {
    t6 = <AnalyzeDataSheetTableCell locale={locale} value={item.payload.downWeightWithoutSpringSupport} />;
    $[14] = item.payload.downWeightWithoutSpringSupport;
    $[15] = locale;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== item.payload.upWeight || $[18] !== locale) {
    t7 = <AnalyzeDataSheetTableCell locale={locale} value={item.payload.upWeight} />;
    $[17] = item.payload.upWeight;
    $[18] = locale;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== item.payload.frontWeight || $[21] !== locale) {
    t8 = <AnalyzeDataSheetTableCell locale={locale} value={item.payload.frontWeight} />;
    $[20] = item.payload.frontWeight;
    $[21] = locale;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== item.payload.strikeWeight || $[24] !== locale) {
    t9 = <AnalyzeDataSheetTableCell locale={locale} value={item.payload.strikeWeight} />;
    $[23] = item.payload.strikeWeight;
    $[24] = locale;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== item.payload.measuredStrikeWeightRatio || $[27] !== locale || $[28] !== shouldUseManualSWRMeasurements) {
    t10 = shouldUseManualSWRMeasurements && <AnalyzeDataSheetTableCell isRatio={true} locale={locale} value={item.payload.measuredStrikeWeightRatio} />;
    $[26] = item.payload.measuredStrikeWeightRatio;
    $[27] = locale;
    $[28] = shouldUseManualSWRMeasurements;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== item.payload.balanceWeight || $[31] !== locale) {
    t11 = <AnalyzeDataSheetTableCell locale={locale} value={item.payload.balanceWeight} />;
    $[30] = item.payload.balanceWeight;
    $[31] = locale;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== item.payload || $[34] !== locale || $[35] !== shouldUseSupportSpringMeasurements) {
    t12 = shouldUseSupportSpringMeasurements && <AnalyzeDataSheetTableCell locale={locale} value={feltBalanceWeight(item.payload)} />;
    $[33] = item.payload;
    $[34] = locale;
    $[35] = shouldUseSupportSpringMeasurements;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== item.payload.frictionWeight || $[38] !== locale) {
    t13 = <AnalyzeDataSheetTableCell locale={locale} value={item.payload.frictionWeight} />;
    $[37] = item.payload.frictionWeight;
    $[38] = locale;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== item.payload.computedStrikeWeightRatio || $[41] !== locale) {
    t14 = <AnalyzeDataSheetTableCell isRatio={true} locale={locale} value={item.payload.computedStrikeWeightRatio} />;
    $[40] = item.payload.computedStrikeWeightRatio;
    $[41] = locale;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== item.payload.supportSpringBalanceWeight || $[44] !== locale || $[45] !== shouldUseSupportSpringMeasurements) {
    t15 = shouldUseSupportSpringMeasurements && <AnalyzeDataSheetTableCell locale={locale} value={item.payload.supportSpringBalanceWeight} />;
    $[43] = item.payload.supportSpringBalanceWeight;
    $[44] = locale;
    $[45] = shouldUseSupportSpringMeasurements;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  let t16;
  if ($[47] !== t1 || $[48] !== t10 || $[49] !== t11 || $[50] !== t12 || $[51] !== t13 || $[52] !== t14 || $[53] !== t15 || $[54] !== t2 || $[55] !== t4 || $[56] !== t5 || $[57] !== t6 || $[58] !== t7 || $[59] !== t8 || $[60] !== t9) {
    t16 = <tr className={t1}>{t2}{t4}{t5}{t6}{t7}{t8}{t9}{t10}{t11}{t12}{t13}{t14}{t15}</tr>;
    $[47] = t1;
    $[48] = t10;
    $[49] = t11;
    $[50] = t12;
    $[51] = t13;
    $[52] = t14;
    $[53] = t15;
    $[54] = t2;
    $[55] = t4;
    $[56] = t5;
    $[57] = t6;
    $[58] = t7;
    $[59] = t8;
    $[60] = t9;
    $[61] = t16;
  } else {
    t16 = $[61];
  }
  return t16;
};